import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type ColorScheme = 'indigo' | 'green' | 'purple' | 'blue' | 'amber' | 'gray';

interface HeaderPaginationProps {
  currentPage: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  colorScheme?: ColorScheme;
}

const colorStyles: Record<
  ColorScheme,
  { hover: string; icon: string; text: string }
> = {
  indigo: {
    hover: 'hover:bg-indigo-100',
    icon: 'text-indigo-700',
    text: 'text-indigo-800',
  },
  green: {
    hover: 'hover:bg-green-100',
    icon: 'text-green-700',
    text: 'text-green-800',
  },
  purple: {
    hover: 'hover:bg-purple-100',
    icon: 'text-purple-700',
    text: 'text-purple-800',
  },
  blue: {
    hover: 'hover:bg-blue-100',
    icon: 'text-blue-700',
    text: 'text-blue-800',
  },
  amber: {
    hover: 'hover:bg-amber-100',
    icon: 'text-amber-700',
    text: 'text-amber-800',
  },
  gray: {
    hover: 'hover:bg-gray-200',
    icon: 'text-gray-600',
    text: 'text-gray-700',
  },
};

export const HeaderPagination: React.FC<HeaderPaginationProps> = ({
  currentPage,
  canGoPrevious,
  canGoNext,
  onPreviousPage,
  onNextPage,
  colorScheme = 'indigo',
}) => {
  const { t } = useTranslation();
  const colors = colorStyles[colorScheme];

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={onPreviousPage}
        disabled={!canGoPrevious}
        className={`p-1 rounded ${colors.hover} disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
        title={t('common.previous', 'Previous')}
      >
        <ChevronLeft className={`w-5 h-5 ${colors.icon}`} />
      </button>
      <span
        className={`text-sm font-medium ${colors.text} min-w-[3rem] text-center`}
      >
        {currentPage}
      </span>
      <button
        onClick={onNextPage}
        disabled={!canGoNext}
        className={`p-1 rounded ${colors.hover} disabled:opacity-30 disabled:cursor-not-allowed transition-colors`}
        title={t('common.next', 'Next')}
      >
        <ChevronRight className={`w-5 h-5 ${colors.icon}`} />
      </button>
    </div>
  );
};
